import { Button } from '@optools/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@optools/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@optools/components/ui/popover';
import { ScrollArea } from '@optools/components/ui/scroll-area';
import { cn } from '@optools/components/ui/utils';
import { Check, ChevronDownIcon } from 'lucide-react';
import * as React from 'react';

export interface ComboBoxValue {
  label: string;
  value: string;
}

export interface ComboBoxProps extends React.ComponentProps<typeof Button> {
  className?: string;
  data: ComboBoxValue[];
  'data-testid'?: string;
  disabled?: boolean;
  onValueChange: (value: string) => void;
  useIndexInKey?: boolean;
  value: string;
}

export const Combobox = ({
  className,
  data,
  'data-testid': testid,
  onValueChange,
  ref,
  useIndexInKey = false,
  value,
  ...props
}: ComboBoxProps) => {
  const [open, setOpen] = React.useState(false);

  return (
    <Popover modal={true} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          aria-expanded={open}
          className={cn('[&_svg:not([class*="text-"])]:text-muted-foreground', className)}
          data-testid={testid}
          role="combobox"
          variant="outline"
          {...props}>
          {value ? data.find((item) => item.value === value)?.label : 'Select item'}
          <ChevronDownIcon className="ml-auto size-4 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-full min-w-(--radix-popper-anchor-width) p-0">
        <Command>
          <CommandInput placeholder="Search items..." />
          <ScrollArea>
            <CommandList>
              <CommandEmpty>No items found</CommandEmpty>
              <CommandGroup>
                {data.map((item, index) => (
                  <CommandItem
                    key={useIndexInKey ? `${item.value}-${index}` : item.value}
                    value={item.value}
                    onSelect={(currentValue) => {
                      onValueChange(currentValue);
                      setOpen(false);
                    }}>
                    <Check
                      className={cn('h-4 w-4', value === item.value ? 'opacity-100' : 'opacity-0')}
                    />
                    {item.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </ScrollArea>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
